import React, { useRef } from "react";
import PropTypes from "prop-types";
import {
  AlertTriangle,
  BarChart,
  Check,
  ClipboardList,
  Cloud,
  CreditCard,
  Download,
  FileSearch,
  FileText,
  GitCompare,
  Scale,
  Siren,
  X
} from "lucide-react";
import html2pdf from "html2pdf.js";
import Logo from "./Logo";
import underwritingData from "../mocks/underwriting_results_UPDATED_MOCK.json";
import auditData from "../mocks/underwriting_audits_mock.json";
import forensicData from "../mocks/underwriting_results_gemini_forensic.json";

// Small helpers to make parsing more generic and resilient
const asArray = (val) => (Array.isArray(val) ? val : []);
const numVal = (val, fallback = 0) => {
  if (typeof val === "number") return val;
  const parsed = Number(String(val || "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(parsed) ? parsed : fallback;
};
const formatNumber = (val) => numVal(val, 0).toLocaleString();

const extractMatch = (text, regex) => {
  if (!text) return "";
  const match = text.match(regex);
  return match ? String(match[1] || "").trim() : "";
};

const parseMoney = (text) => numVal(text, 0);

const parseMarkdownLines = (markdown) =>
  String(markdown || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

const getSection = (data, keys) => {
  for (const key of keys) {
    if (data && Object.prototype.hasOwnProperty.call(data, key)) {
      return data[key];
    }
  }
  return null;
};

const normalizeGeminiReport = (inputData) => {
  if (!inputData) return inputData;

  const metaSection = getSection(inputData, ["0_meta_data", "0) Meta Data", "0) Meta data"]);
  const execSection = getSection(inputData, ["1_executive_summary", "1) Executive Summary", "1) Executive summary"]);
  const borrowersSection = getSection(inputData, ["2_borrowers_info", "2) Borrowers Info", "2) Borrowers info"]) || {};
  const businessSection = getSection(inputData, ["3_business_entity_analysis", "3) Business Entity Analysis", "3) Business entity analysis"]) || {};
  const propertiesSection = getSection(inputData, ["4_real_estate_owned", "4) Real Estate Owned", "4) Real estate owned"]) || {};
  const liabilitiesSection = getSection(inputData, ["5_liabilities", "5) Liabilities", "5) liabilities"]) || {};
  const incomeSection = getSection(inputData, ["6_income_summary", "6) Income Summary", "6) Income summary"]) || {};
  const reservesSection = getSection(inputData, ["7_reserves_dti", "7) Reserves & DTI", "7) Reserves and DTI"]) || {};
  const riskSection = getSection(inputData, ["8_risk_observations", "8) Risk Observations", "8) Risk observations"]) || {};
  const nextStepsSection = getSection(inputData, ["9_next_steps_conditions", "9) Next Steps / Conditions", "9) Next Steps", "9) Next steps / conditions"]) || {};
  const auditSection = getSection(inputData, ["10_auditing", "10) Auditing", "10) Audit"]) || {};
  const forensicSection = getSection(inputData, ["11_forensic_accounting", "11) Forensic Accounting", "11) Forensic accounting"]);
  const referencesSection = getSection(inputData, ["12_references", "12) References", "12) Reference"]) || [];
  const crossVerificationSection = getSection(inputData, ["13_cross_verification_findings", "13) Cross-Verification Findings", "13) Cross-verification findings"]) || {};
  const creditDeepDiveSection = getSection(inputData, ["14_credit_report_deep_dive", "14) Credit Report Deep Dive", "14) Credit report deep dive"]) || {};

  const hasGeminiSections = Boolean(
    metaSection ||
    execSection ||
    borrowersSection?.markdown ||
    businessSection?.markdown ||
    propertiesSection?.markdown
  );
  if (!hasGeminiSections) {
    return inputData;
  }

  const borrowersMarkdown = borrowersSection.markdown || "";
  const primaryBorrower = extractMatch(borrowersMarkdown, /Primary Borrower:\s*([^\n]+)/i);
  const coBorrower = extractMatch(borrowersMarkdown, /Co-?Borrower:\s*([^\n]+)/i);
  const employer = extractMatch(borrowersMarkdown, /Employer:\s*([^\n]+)/i);

  const borrowers = [
    {
      name: primaryBorrower || "Borrower",
      role: "Primary",
      employment: employer ? { employer } : null
    }
  ];
  if (coBorrower) {
    borrowers.push({ name: coBorrower, role: "Co-Borrower" });
  }

  const businessMarkdown = businessSection.markdown || "";
  const businessName = extractMatch(businessMarkdown, /###\s*([^\n]+)/);
  const grossReceipts = parseMoney(extractMatch(businessMarkdown, /Gross Receipts:\s*\$?([0-9,.-]+)/i));
  const totalDeductions = parseMoney(extractMatch(businessMarkdown, /Total Deductions:\s*\$?([0-9,.-]+)/i));
  const netLoss = parseMoney(extractMatch(businessMarkdown, /Net Loss:\s*\(?\$?([0-9,.-]+)\)?/i));

  const businesses = businessName
    ? [
        {
          name: businessName,
          "2024_tax_return": {
            gross_receipts: grossReceipts,
            total_deductions: totalDeductions,
            ordinary_income_loss: netLoss ? -Math.abs(netLoss) : 0
          }
        }
      ]
    : [];

  const propertiesLines = parseMarkdownLines(propertiesSection.markdown);
  const properties = propertiesLines
    .filter((line) => /^\d+\./.test(line))
    .map((line) => {
      const address = extractMatch(line, /\*\*(.+?)\*\*/);
      const rent = parseMoney(extractMatch(line, /Lease:\s*\$?([0-9,.-]+)\/mo/i));
      const servicer = extractMatch(line, /Mortgage:\s*([^($]+)/i);
      const payment = parseMoney(extractMatch(line, /Mortgage:[^$]*\$([0-9,.-]+)/i));
      return {
        address,
        loan: {
          servicer: servicer || "Servicer",
          payment_due: payment
        },
        lease: rent ? { monthly_rent: rent } : {}
      };
    })
    .filter((property) => property.address);

  const liabilitiesLines = parseMarkdownLines(liabilitiesSection.markdown);
  const mortgage_payments_observed = liabilitiesLines
    .filter((line) => /\*\*/.test(line))
    .map((line) => ({
      creditor: extractMatch(line, /\*\*(.+?)\*\*/),
      amount: parseMoney(line)
    }))
    .filter((item) => item.creditor);

  const incomeMarkdown = incomeSection.markdown || "";
  const monthlyAverage = parseMoney(extractMatch(incomeMarkdown, /Monthly Average:\s*\$?([0-9,.-]+)/i));
  const grossPay = parseMoney(extractMatch(incomeMarkdown, /Gross Pay:\s*\$?([0-9,.-]+)/i));
  const baseEmploymentMonthly = monthlyAverage || (grossPay ? grossPay / 12 : 0);

  const reservesLines = parseMarkdownLines(reservesSection.markdown);
  const reserves = reservesLines
    .filter((line) => line.startsWith("*") && /\*\*/.test(line))
    .map((line) => ({
      assets_considered: extractMatch(line, /\*\*(.+?)\*\*/),
      months_vs_estimated_PITI_stack: ""
    }))
    .filter((reserve) => reserve.assets_considered);

  const dtiPercent = numVal(extractMatch(reservesSection.markdown || "", /Back-End DTI:\s*\*\*~?([0-9.]+)%/i), 0);

  const riskObservations = [
    ...(riskSection.key_insight || []),
    ...(riskSection.warnings || [])
  ];

  const nextSteps = parseMarkdownLines(nextStepsSection.markdown)
    .filter((line) => /^\d+\./.test(line))
    .map((line) => line.replace(/^\d+\.\s*/, ""));

  const auditSub = auditSection.substructure || {};
  const extractFindings = (items) =>
    (items || []).map((item) => {
      const [id, ...rest] = String(item).split(":");
      const label = rest.length ? rest.join(":").trim() : String(item);
      return {
        finding_id: id.trim(),
        title: label || id.trim(),
        description: label || id.trim()
      };
    });

  const dtiStress = String(auditSub.dti_stress_test || "");
  const optimisticDti = numVal(extractMatch(dtiStress, /Optimistic DTI[^0-9]*([0-9.]+)%/i), 0);
  const realisticDti = numVal(extractMatch(dtiStress, /Realistic DTI[^0-9]*([0-9.]+)%/i), 0);

  return {
    "0_meta_data": metaSection || inputData["0_meta_data"],
    "1_executive_summary": typeof execSection === "string" ? execSection : inputData["1_executive_summary"],
    "2_borrowers_info": borrowersSection,
    "3_business_entity_analysis": businessSection,
    "4_real_estate_owned": propertiesSection,
    "5_liabilities": liabilitiesSection,
    "6_income_summary": incomeSection,
    "7_reserves_dti": reservesSection,
    "8_risk_observations": riskSection,
    "9_next_steps_conditions": nextStepsSection,
    "10_auditing": auditSection,
    "11_forensic_accounting": forensicSection,
    "12_references": referencesSection,
    "13_cross_verification_findings": crossVerificationSection,
    "14_credit_report_deep_dive": creditDeepDiveSection,
    borrowers,
    businesses,
    properties,
    liabilities_monthly_estimate: {
      mortgage_payments_observed,
      total_estimated_monthly_mortgage_debt: mortgage_payments_observed.reduce(
        (sum, item) => sum + numVal(item.amount),
        0
      )
    },
    income_summary_for_uw: {
      base_employment_monthly: baseEmploymentMonthly
    },
    reserves_estimate: {
      months_of_reserves: reserves
    },
    dti_analysis_revised: {
      dti_percent: dtiPercent
    },
    risk_observations: riskObservations,
    next_steps_conditions: nextSteps,
    documents_referenced: referencesSection,
    underwriting_review: {
      metadata: {
        institution: "CloudMortgage.io",
        risk_rating: "Review",
        review_status: "PENDING"
      },
      executive_summary: {
        overall_assessment: auditSection.summary || ""
      },
      critical_findings: extractFindings(auditSub.critical_findings),
      data_quality_issues: extractFindings(auditSub.data_quality_gaps),
      compliance_best_practice_issues: extractFindings(auditSub.compliance_issues),
      debt_to_income_analysis: {
        best_case_scenario: {
          scenario_name: "Optimistic",
          dti_ratio: optimisticDti,
          monthly_income: {
            total_qualifying_income: baseEmploymentMonthly
          }
        },
        conservative_scenario: {
          scenario_name: "Realistic",
          dti_ratio: realisticDti,
          monthly_income: {
            total_qualifying_income: baseEmploymentMonthly
          }
        }
      },
      conditions_precedent: {
        tier_1_critical: {
          conditions: (auditSub.conditions_precedent || [])
            .filter((condition) => condition.startsWith("Tier 1"))
            .map((condition) => ({ requirement: condition }))
        },
        tier_2_important: {
          conditions: (auditSub.conditions_precedent || [])
            .filter((condition) => condition.startsWith("Tier 2"))
            .map((condition) => ({ requirement: condition }))
        }
      },
      final_recommendation: {
        decision: "PENDING",
        rationale: auditSection.summary || ""
      }
    }
  };
};

const getFinancials = (inputData) => {
  const data = inputData || underwritingData || {};
  const borrowers = asArray(data.borrowers).map((b) => ({
    ...b,
    employment: b?.employment || b?.employment_details || null
  }));
  const businesses = asArray(data.businesses).map((biz) => ({
    ...biz,
    tax_return_2024: biz["2024_tax_return"] || biz.tax_return_2024 || biz.tax_return || {}
  }));
  const properties = asArray(data.properties).map((property) => {
    const lease = property.lease || {};
    const leaseTenant = Array.isArray(lease.tenants)
      ? lease.tenants.join(", ")
      : lease.tenant || "";
    const leaseStatus = lease.term
      || (lease.status && lease.effective_date
        ? `${lease.status} (effective ${lease.effective_date})`
        : lease.status)
      || (lease.not_evidenced ? "Lease not evidenced" : "");
    const rentAmount = lease.monthly_rent || lease.proposed_rent || 0;

    return {
      ...property,
      servicer: property.loan?.servicer,
      principal: numVal(
        property.loan?.outstanding_principal_2025_09 ||
        property.loan?.loan_balance ||
        property.loan?.principal ||
        0
      ),
      payment: numVal(property.loan?.payment_due || property.loan?.current_payment || property.loan?.monthly_pitia_current || 0),
      rent: numVal(rentAmount),
      lease_status: leaseStatus,
      lease_tenant: leaseTenant
    };
  });
  const liabilities = data.liabilities_monthly_estimate || {};
  const mortgagePayments = asArray(liabilities.mortgage_payments_observed).map((l) => ({
    creditor: l.creditor || l.servicer || "Servicer",
    amount: numVal(l.amount)
  }));
  const totalFromList = mortgagePayments.reduce((sum, l) => sum + numVal(l.amount), 0);
  const regionalEstimated = numVal(liabilities.regions_bank_estimated_payment || liabilities.regional_estimated || 0);
  const totalEstimated =
    numVal(liabilities.total_estimated_monthly_mortgage_debt) || totalFromList + regionalEstimated;
  const incomeSummary = data.income_summary_for_uw || {};
  const reservesEstimate = data.reserves_estimate || {};
  const dtiAnalysis = data.dti_analysis_revised || {};
  const documentsReferenced = data.documents_referenced || [];
  const documentsReferencedSources = data.documents_referenced_sources || {};
  const crossVerification = data["13_cross_verification_findings"] || {};
  const creditDeepDive = data["14_credit_report_deep_dive"] || {};

  return {
    reportInfo: {
      loanNumber: "1245789",
      generatedDate: new Date().toLocaleDateString()
    },
    borrowers,
    businesses,
    properties,
    liabilities_monthly_estimate: {
      mortgage_payments: mortgagePayments,
      regional_estimated: regionalEstimated,
      total_estimated: totalEstimated
    },
    income_summary_for_uw: {
      base_employment: numVal(incomeSummary.base_employment_monthly || incomeSummary.base_employment || 0),
      rental_income_tax_based: {
        cash_flow_after_int:
          numVal(incomeSummary.rental_income_tax_based?.cash_flow_after_interest_monthly || incomeSummary.rental_income_tax_based?.cash_flow_after_int || 0)
      },
      est_net_rental_cash: numVal(incomeSummary.estimated_net_rental_cash_flow_monthly || incomeSummary.est_net_rental_cash || 0)
    },
    reserves_estimate: {
      months:
        reservesEstimate.months_of_reserves?.map((reserve) => ({
          assets: reserve.assets_considered,
          months_piti: reserve.months_vs_estimated_PITI_stack
        })) || []
    },
    references: {
      documents: documentsReferenced,
      source: documentsReferencedSources
    },
    dti_revised: {
      qualifying_income: dtiAnalysis.qualifying_income_monthly || 0,
      qualifying_debt: dtiAnalysis.qualifying_debt_monthly || 0,
      dti_percent: dtiAnalysis.dti_percent || 0,
      note: dtiAnalysis.note || ""
    },
    risk_observations: data.risk_observations || [],
    next_steps: data.next_steps_conditions || [],
    crossVerification,
    creditDeepDive
  };
};

const getAudit = (inputData) => {
  const review = (inputData || auditData).underwriting_review || {};
  const dti = review.debt_to_income_analysis || {};
  const bestCase = dti.best_case_scenario || {};
  const conservativeCase = dti.conservative_scenario || {};

  return {
    metadata: {
      institution: review.metadata?.institution || "N/A",
      risk: review.metadata?.risk_rating || "N/A",
      status: review.metadata?.review_status || "PENDING"
    },
    executive_summary: {
      assessment: review.executive_summary?.overall_assessment || ""
    },
    critical_findings: (review.critical_findings || []).map((finding) => ({
      severity: finding.severity || "INFO",
      id: finding.finding_id || finding.category || "Finding",
      title: finding.title || finding.category || "Finding",
      desc: finding.description || finding.recommendation || "",
      impact: finding.impact || ""
    })),
    data_quality: (review.data_quality_issues || []).map((issue) => ({
      id: issue.issue_id || issue.category || "Data Quality",
      title: issue.title || issue.category || "Data Quality",
      details: issue.recommendation || issue.concern || ""
    })),
    compliance_issues: (review.compliance_best_practice_issues || []).map((issue) => ({
      id: issue.issue_id || issue.category || "Compliance",
      title: issue.title || issue.category || "Compliance",
      desc: issue.recommendation || ""
    })),
    dti_analysis: {
      optimistic: {
        name: bestCase.scenario_name || "Optimistic",
        dti: bestCase.dti_ratio || 0,
        income: bestCase.monthly_income?.total_qualifying_income || 0
      },
      conservative: {
        name: conservativeCase.scenario_name || "Conservative",
        dti: conservativeCase.dti_ratio || 0,
        income: conservativeCase.monthly_income?.total_qualifying_income || 0
      }
    },
    conditions_precedent: {
      tier1:
        review.conditions_precedent?.tier_1_critical?.conditions?.map(
          (condition) => condition.requirement || condition.condition_id || ""
        ) || [],
      tier2:
        review.conditions_precedent?.tier_2_important?.conditions?.map(
          (condition) => condition.requirement || condition.condition_id || ""
        ) || []
    },
    final_recommendation: {
      decision: review.final_recommendation?.decision || "PENDING",
      rationale:
        review.final_recommendation?.rationale ||
        review.debt_to_income_analysis?.conclusion ||
        ""
    },
    certification:
      review.underwriter_certification?.statement ||
      review.underwriter_certification?.review_completeness ||
      ""
  };
};

const getForensic = (inputData) => {
  const data = inputData || forensicData;
  const applicants = data.borrowers || data.applicants || [];
  const businesses = data.businesses || [];
  const properties = data.properties || [];
  const liabilities = data.liabilities_monthly_estimate || data.liabilities_summary || {};
  const incomeSummary = data.income_summary_for_uw || {};
  const incomeAnalysis = data.income_analysis || {};

  const bankBusinessAccounts = businesses.map((biz) => {
    const summaries = [];
    const yr = biz.banking?.year_2024_summary;
    if (yr) {
      summaries.push({
        year: 2024,
        total_deposits: yr.total_deposits || 0,
        total_withdrawals: yr.total_withdrawals || 0,
        net_cash_flow: yr.net_cash_flow || 0,
        ending_balance: yr.end_balance || yr.ending_balance || 0
      });
    }
    const selected = Array.isArray(biz.banking?.selected_months_2025) ? biz.banking.selected_months_2025 : [];
    const ending = selected.length ? selected[selected.length - 1].end_balance : undefined;
    if (summaries.length && ending !== undefined) {
      summaries[0].ending_balance = ending;
    }
    return {
      institution: biz.banking?.institution || biz.name || "Business Account",
      account_type: biz.banking?.account_type || "Business Operating",
      account_last4: biz.banking?.account_no_masked || "----",
      annual_summaries: summaries,
      notes: biz.authority_verification_required || ""
    };
  });

  const personalAccounts = [];
  applicants.forEach((applicant) => {
    (applicant.personal_bank_accounts || []).forEach((acct) => {
      personalAccounts.push({
        institution: acct.institution,
        account_type: acct.account_type || "Checking",
        account_last4: acct.account_no_masked || "----",
        monthly_snapshots: (acct.periods || []).map((p) => ({
          period: p.period,
          deposits: p.deposits,
          withdrawals: p.withdrawals,
          ending_balance: p.end_balance
        })),
        notes: acct.notes || ""
      });
    });
  });

  const bankAnalysis = data.bank_accounts_analysis || {};
  const bank = {
    business_accounts:
      (bankAnalysis.business_accounts && bankAnalysis.business_accounts.length
        ? bankAnalysis.business_accounts
        : bankBusinessAccounts) || [],
    personal_accounts:
      (bankAnalysis.personal_accounts && bankAnalysis.personal_accounts.length
        ? bankAnalysis.personal_accounts
        : personalAccounts) || []
  };

  const investments =
    data.investment_assets ||
    (data.reserves_estimate?.months_of_reserves || []).map((reserve) => ({
      account: reserve.assets_considered,
      owner: applicants[0]?.name || "Borrower",
      value_history: [],
      notes: `Counts toward reserves (${reserve.months_vs_estimated_PITI_stack} months PITI).`
    }));

  const risks =
    data.risk_and_trends ||
    (data.risk_observations || []).map((r, idx) => ({
      risk_id: `FR-${String(idx + 1).padStart(2, "0")}`,
      category: "Forensic Observation",
      description: r,
      severity: "Medium"
    }));

  const followUps = data.required_follow_ups || data.next_steps_conditions || [];

  const income = incomeAnalysis && Object.keys(incomeAnalysis).length
    ? {
      ...incomeAnalysis,
      qualifying_income_summary:
        incomeAnalysis.qualifying_income_summary ||
        (incomeSummary.base_employment_monthly
          ? { base_employment_income_gross_monthly: incomeSummary.base_employment_monthly }
          : {})
    }
    : {
      qualifying_income_summary: {
        base_employment_income_gross_monthly: incomeSummary.base_employment_monthly || 0,
        notes:
          "Primary qualifying income from W-2. Rental cash flow adjusted for observed PITIA and estimated Regions obligation."
      }
    };

  const documents =
    data.documents_referenced ||
    data.references?.documents ||
    [];

  return {
    applicants,
    businesses,
    bank,
    investments,
    risks,
    followUps,
    income,
    liabilities,
    documents
  };
};

const SampleSummaryModal = ({ isOpen, onClose, autoDownload = false, reportData }) => {
  const reportRef = useRef(null);
  const hasAutoDownloaded = useRef(false);

  const normalizedReportData = React.useMemo(() => normalizeGeminiReport(reportData), [reportData]);
  const report1_Financials = React.useMemo(() => getFinancials(normalizedReportData), [normalizedReportData]);
  const report2_Audit = React.useMemo(() => getAudit(normalizedReportData), [normalizedReportData]);
  const report3_Forensic = React.useMemo(() => getForensic(normalizedReportData), [normalizedReportData]);

  const borrowerName = report1_Financials.borrowers[0]?.name || "Borrower";
  const borrowerFirstName = borrowerName.split(" ")[0] || "Borrower";
  const fileName = `Full_Underwriting_Package_${borrowerFirstName}.pdf`;

  const handleDownload = () => {
    if (!reportRef.current) return;

    const options = {
      margin: 0.5,
      filename: fileName,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
      pagebreak: { mode: ["css", "legacy"], avoid: ".pdf-avoid-break" }
    };

    html2pdf().from(reportRef.current).set(options).save();
  };

  React.useEffect(() => {
    if (isOpen && autoDownload && !hasAutoDownloaded.current) {
      hasAutoDownloaded.current = true;
      setTimeout(handleDownload, 300);
    }
    if (!isOpen) {
      hasAutoDownloaded.current = false;
    }
  }, [isOpen, autoDownload]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-slate-900/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-5xl h-[95vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-slate-700"
        onClick={(e) => e.stopPropagation()}
      >

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50 shrink-0">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-sky-100 rounded-lg border border-sky-200">
              <FileText className="w-6 h-6 text-sky-600" />
            </div>
            <div>
              <h3 className="font-bold text-lg text-slate-800">{fileName}</h3>
              <p className="text-sm text-slate-500">Loan #{report1_Financials.reportInfo.loanNumber} • Generated Now</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-sky-600 rounded-lg shadow-sm hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 transition-all"
            >
              <Download className="w-4 h-4" />
              Download
            </button>
            <button onClick={onClose} className="p-2 rounded-lg text-slate-500 hover:bg-slate-200 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 bg-white overflow-y-auto p-0 sm:p-0 flex justify-center">
          <div ref={reportRef} className="w-full bg-white text-slate-800 font-sans text-base leading-relaxed min-h-[2300px]">

            {/* ==================== REPORT 1: FINANCIAL ANALYSIS ==================== */}
            <div className="p-8">
              {/* Header */}
              <div className="flex justify-between items-start mb-10 border-b-4 border-slate-800 pb-6">
                <div className="flex gap-4 items-center">
                  <div className="h-16 flex items-center bg-slate-900 rounded-lg px-4 py-2">
                    <Logo className="h-12 w-auto" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-extrabold text-slate-900">A.I. Underwriting Report</h1>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Financial Report</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-semibold text-slate-700">Loan #{report1_Financials.reportInfo.loanNumber}</div>
                  <div className="text-sm text-slate-500">{report1_Financials.reportInfo.generatedDate}</div>
                </div>
              </div>

              {/* 1. Borrowers */}
              <div className="mb-10">
                <h2 className="text-lg font-bold text-sky-800 uppercase border-b-2 border-sky-200 pb-2 mb-4">1. Borrowers</h2>
                {report1_Financials.borrowers.map((b, i) => (
                  <div key={i} className="mb-4 bg-slate-50 p-4 rounded-lg border border-slate-200 shadow-sm">
                    <div className="font-semibold text-lg text-slate-800">{b.name}</div>
                    <div className="text-sm text-slate-600 mb-3">SSN: {b.ssn_masked} • {b.current_address || b.role}</div>
                    {b.employment && (
                      <div className="grid grid-cols-3 gap-4 text-sm text-slate-700 mt-3 border-t border-slate-200 pt-3">
                        <div><span className="font-semibold text-slate-600">Employer:</span> {b.employment.employer}</div>
                        <div><span className="font-semibold text-slate-600">W-2 Wages:</span> ${formatNumber(b.employment?.w2_wages_2024)}</div>
                        <div><span className="font-semibold text-slate-600">Medicare:</span> ${formatNumber(b.employment?.medicare_wages_2024)}</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* 2. Business & Banking */}
              <div className="mb-10">
                <h2 className="text-lg font-bold text-sky-800 uppercase border-b-2 border-sky-200 pb-2 mb-4">2. Business Entity Analysis</h2>
                {report1_Financials.businesses.map((biz, i) => (
                  <div key={i} className="space-y-6">
                    <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 shadow-sm">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-semibold text-lg text-slate-800">{biz.name}</div>
                          <div className="text-sm text-slate-600 mt-1">{biz.type}</div>
                        </div>
                        <div className="text-xs bg-sky-100 text-sky-800 px-2 py-0.5 rounded-full font-medium">EIN: {biz.ein}</div>
                      </div>
                      <div className="text-sm text-red-600 font-bold mt-2 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        <span className="text-sm text-red-700">Authority Issue: {biz.authority_verification_required}</span>
                      </div>
                    </div>

                    {/* Banking Table */}
                    <div className="border border-slate-200 rounded-lg overflow-hidden shadow-sm">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-100 font-bold text-slate-700">
                          <tr><th className="p-3 text-left text-sm font-semibold text-slate-700">Period</th><th className="p-3 text-right text-sm font-semibold text-slate-700">Deposits</th><th className="p-3 text-right text-sm font-semibold text-slate-700">Withdrawals</th><th className="p-3 text-right text-sm font-semibold text-slate-700">End Balance</th></tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200">
                        {(biz.banking?.selected_months_2025 || []).map((m, idx) => (
                            <tr key={idx} className="hover:bg-slate-50 text-sm text-slate-700">
                              <td className="p-3">{m.period}</td>
                              <td className="p-3 text-right text-green-700">+${formatNumber(m.deposits)}</td>
                              <td className="p-3 text-right text-red-700">-${formatNumber(m.withdrawals)}</td>
                              <td className="p-3 text-right font-semibold">${formatNumber(m.end_balance)}</td>
                            </tr>
                          ))}
                          <tr className="bg-slate-100 font-semibold text-sm text-slate-800">
                            <td className="p-3">2024 Full Year</td>
                            <td className="p-3 text-right">+${formatNumber(biz.banking?.year_2024_summary?.total_deposits)}</td>
                            <td className="p-3 text-right">-${formatNumber(biz.banking?.year_2024_summary?.total_withdrawals)}</td>
                            <td className="p-3 text-right">Net: ${formatNumber(biz.banking?.year_2024_summary?.net_cash_flow)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Tax Return Analysis */}
                    <div className="grid grid-cols-2 gap-6 text-sm">
                      <div className="p-4 border border-slate-200 rounded-lg shadow-sm">
                        <div className="font-semibold text-base border-b pb-2 mb-2 text-slate-700">Form 1065 Summary</div>
                        <div className="flex justify-between py-1"><span>Gross Receipts:</span><span>${formatNumber(biz.tax_return_2024?.form_1065_summary?.gross_receipts_reported)}</span></div>
                        <div className="flex justify-between py-1 text-red-600"><span>Ord. Loss:</span><span>${formatNumber(biz.tax_return_2024?.form_1065_summary?.ordinary_business_income_loss)}</span></div>
                      </div>
                      <div className="p-4 border border-slate-200 rounded-lg shadow-sm">
                        <div className="font-semibold text-base border-b pb-2 mb-2 text-slate-700">Form 8825 Detail</div>
                        <div className="flex justify-between py-1"><span>Gross Rents:</span><span>${formatNumber(biz.tax_return_2024?.form_8825_detail?.gross_rents)}</span></div>
                        <div className="flex justify-between py-1 text-green-700 font-bold"><span>NOI:</span><span>${formatNumber(biz.tax_return_2024?.form_8825_detail?.noi_before_interest_depr)}</span></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* 3. Properties */}
              <div className="mb-10">
                <h2 className="text-lg font-bold text-sky-800 uppercase border-b-2 border-sky-200 pb-2 mb-4">3. Real Estate Owned</h2>
                <div className="border border-slate-200 rounded-lg overflow-hidden shadow-sm">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100 font-bold text-slate-700">
                      <tr>
                        <th className="p-3 text-left text-sm font-semibold text-slate-700">Address</th>
                        <th className="p-3 text-left text-sm font-semibold text-slate-700">Servicer</th>
                        <th className="p-3 text-right text-sm font-semibold text-slate-700">Balance</th>
                        <th className="p-3 text-right text-sm font-semibold text-slate-700">Payment</th>
                        <th className="p-3 text-right text-sm font-semibold text-slate-700">Rent</th>
                        <th className="p-3 text-left text-sm font-semibold text-slate-700">Lease Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {report1_Financials.properties.map((p, i) => (
                        <tr key={i} className={`hover:bg-slate-50 ${p.payment === 0 ? "bg-red-50" : ""}`}>
                          <td className="p-3 text-sm text-slate-700 font-medium">{p.address}</td>
                          <td className="p-3 text-sm text-slate-700">{p.servicer}</td>
                          <td className="p-3 text-right text-sm text-slate-700">${formatNumber(p.principal || p.balance)}</td>
                          <td className="p-3 text-right text-red-600 text-sm font-medium">${formatNumber(p.payment)}</td>
                          <td className="p-3 text-right text-green-600 text-sm font-medium">${formatNumber(p.rent)}</td>
                          <td className="p-3 text-sm text-slate-700 whitespace-normal break-words">{p.lease_tenant || p.lease_status || "N/A"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 4. Liabilities & Income Summary */}
              <div className="grid grid-cols-2 gap-10 mb-10">
                <div>
                  <h2 className="text-lg font-bold text-sky-800 uppercase border-b-2 border-sky-200 pb-2 mb-4">4. Liabilities</h2>
                  <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 shadow-sm">
                    <div className="space-y-2 text-sm">
                      {report1_Financials.liabilities_monthly_estimate.mortgage_payments.map((l, i) => (
                        <div key={i} className="flex items-center justify-between border-b border-slate-100 py-2 text-slate-700 gap-3">
                          <span className="text-slate-600 break-words max-w-[65%]">{l.creditor}</span>
                          <span className="font-mono text-red-600 font-medium text-right">${formatNumber(l.amount)}</span>
                        </div>
                      ))}
                      <div className="flex items-center justify-between font-semibold pt-2 text-sm text-slate-700 gap-3">
                        <span className="break-words max-w-[65%]">Regional Bank (Est):</span>
                        <span className="font-mono text-red-600 text-right">${formatNumber(report1_Financials.liabilities_monthly_estimate.regional_estimated)}</span>
                      </div>
                      <div className="flex items-center justify-between font-bold text-base bg-slate-100 p-3 rounded-lg mt-2 text-slate-800 gap-3">
                        <span className="break-words max-w-[65%]">Total Monthly Debt:</span>
                        <span className="font-mono text-right">${formatNumber(report1_Financials.liabilities_monthly_estimate.total_estimated)}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <h2 className="text-lg font-bold text-sky-800 uppercase border-b-2 border-sky-200 pb-2 mb-4">5. Income Summary</h2>
                  <div className="bg-slate-50 p-4 rounded-lg text-sm space-y-3 border border-slate-200 shadow-sm">
                    <div className="flex justify-between text-sm text-slate-700"><span>Base Employment:</span> <span className="font-medium">${formatNumber(report1_Financials.income_summary_for_uw.base_employment)}</span></div>
                    <div className="flex justify-between text-sm text-slate-700"><span>Net Rental (Tax Method):</span> <span className="font-medium">${formatNumber(report1_Financials.income_summary_for_uw.rental_income_tax_based.cash_flow_after_int)}</span></div>
                    <div className="flex justify-between text-sm text-slate-700"><span>Net Cash Flow (Est):</span> <span className="font-medium">${formatNumber(report1_Financials.income_summary_for_uw.est_net_rental_cash)}</span></div>
                    <div className="border-t border-slate-300 pt-3 mt-3">
                      <div className="flex justify-between text-base font-bold text-green-700">
                        <span>Total Qualifying (Tax):</span>
                        <span>${formatNumber(report1_Financials.income_summary_for_uw.base_employment + report1_Financials.income_summary_for_uw.rental_income_tax_based.cash_flow_after_int)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 6. Reserves & DTI */}
              <div className="mb-10 break-before-page pt-4">
                <h2 className="text-lg font-bold text-sky-800 uppercase border-b-2 border-sky-200 pb-2 mb-4">6. Reserves & DTI</h2>
                <div className="grid grid-cols-2 gap-6">
                  <div className="bg-slate-50 p-4 rounded-lg text-sm border border-slate-200 shadow-sm">
                    <div className="font-semibold text-base mb-3 text-slate-800">Reserves Estimate</div>
                    {report1_Financials.reserves_estimate.months.map((r, i) => (
                      <div key={i} className="flex justify-between py-1 border-b border-slate-200">
                        <span className="text-sm text-slate-700">{r.assets}:</span>
                        <span className="font-semibold text-sm text-slate-800">{r.months_piti} Months</span>
                      </div>
                    ))}
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg text-sm border border-red-200 shadow-sm">
                    <div className="font-semibold text-base text-red-800 mb-3">DTI Analysis</div>
                    <div className="flex justify-between py-1 text-sm text-slate-700"><span>Qualifying Income:</span> <span className="font-medium">${formatNumber(report1_Financials.dti_revised.qualifying_income)}</span></div>
                    <div className="flex justify-between py-1 text-sm text-slate-700"><span>Qualifying Debt:</span> <span className="font-medium">${formatNumber(report1_Financials.dti_revised.qualifying_debt)}</span></div>
                    <div className="text-center mt-3 pt-3 border-t border-red-200">
                      <div className="text-4xl font-extrabold text-red-600">{report1_Financials.dti_revised.dti_percent}%</div>
                      <div className="text-xs text-red-700 mt-1">{report1_Financials.dti_revised.note}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 7. Risk Observations & Next Steps */}
              <div className="grid grid-cols-2 gap-10 pdf-avoid-break">
                <div>
                  <h2 className="text-lg font-bold text-sky-800 uppercase border-b-2 border-sky-200 pb-2 mb-4">7. Risk Observations</h2>
                  <ul className="list-disc list-inside text-sm space-y-2 text-slate-700">
                    {report1_Financials.risk_observations.map((r, i) => <li key={i} className="pl-2">{r}</li>)}
                  </ul>
                </div>
                <div>
                  <h2 className="text-lg font-bold text-sky-800 uppercase border-b-2 border-sky-200 pb-2 mb-4">8. Next Steps / Conditions</h2>
                  <ul className="list-decimal list-inside text-sm space-y-2 text-slate-700">
                    {report1_Financials.next_steps.slice(0, 6).map((s, i) => (
                      <li key={i} className="pl-2">{s}</li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* 9. References */}
              <div className="mt-10 break-before-page">
                <h2 className="text-lg font-bold text-sky-800 uppercase border-b-2 border-sky-200 pb-2 mb-4">9. References</h2>
                <div className="bg-white border border-slate-200 rounded-lg p-4 text-sm text-slate-700 space-y-2 shadow-sm">
                  {report1_Financials.references.documents && report1_Financials.references.documents.length > 0 ? (
                    <ul className="list-disc list-inside space-y-1 text-sm break-words">
                      {report1_Financials.references.documents.map((doc, idx) => (
                        <li key={idx} className="pl-2 break-words">{doc}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-slate-500 italic">No references available.</p>
                  )}
                  {(report1_Financials.references.source?.location || report1_Financials.references.source?.description) && (
                    <div className="pt-3 border-t border-slate-200 text-slate-500">
                      <div className="font-semibold text-sm text-slate-700">Source</div>
                      {report1_Financials.references.source.location && (
                        <p className="text-xs">Location: {report1_Financials.references.source.location}</p>
                      )}
                      {report1_Financials.references.source.description && (
                        <p className="text-xs">{report1_Financials.references.source.description}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* 10. Cross-Verification Findings */}
              {report1_Financials.crossVerification && Object.keys(report1_Financials.crossVerification).length > 0 && (
                <div className="mt-10">
                  <h2 className="text-lg font-bold text-sky-800 uppercase border-b-2 border-sky-200 pb-2 mb-4 flex items-center gap-2">
                    <GitCompare className="w-5 h-5" /> 10. Cross-Verification Findings
                  </h2>

                  {/* Summary */}
                  {report1_Financials.crossVerification.summary && (
                    <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 shadow-sm mb-6">
                      <p className="text-sm text-slate-700">{report1_Financials.crossVerification.summary}</p>
                    </div>
                  )}

                  {/* URLA vs Credit Report */}
                  {report1_Financials.crossVerification.urla_vs_credit_report && (
                    <div className="mb-6">
                      <h3 className="text-base font-semibold text-slate-800 mb-3 flex items-center gap-2">
                        <FileText className="w-4 h-4 text-sky-600" /> URLA vs Credit Report
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Matches */}
                        {report1_Financials.crossVerification.urla_vs_credit_report.matches?.length > 0 && (
                          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                            <h4 className="text-sm font-semibold text-green-800 mb-2 flex items-center gap-1">
                              <Check className="w-4 h-4" /> Matches
                            </h4>
                            <ul className="space-y-2">
                              {report1_Financials.crossVerification.urla_vs_credit_report.matches.map((match, idx) => (
                                <li key={idx} className="text-sm text-slate-700">
                                  <span className="font-medium text-slate-800">{match.field}:</span> {match.status}
                                  {match.details && <p className="text-xs text-slate-500 mt-0.5">{match.details}</p>}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {/* Discrepancies */}
                        {report1_Financials.crossVerification.urla_vs_credit_report.discrepancies?.length > 0 && (
                          <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                            <h4 className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-1">
                              <AlertTriangle className="w-4 h-4" /> Discrepancies
                            </h4>
                            <ul className="space-y-2">
                              {report1_Financials.crossVerification.urla_vs_credit_report.discrepancies.map((disc, idx) => (
                                <li key={idx} className="text-sm text-slate-700">
                                  <span className="font-medium text-slate-800">{disc.field}:</span>
                                  <div className="text-xs text-slate-600 mt-0.5">
                                    <span>URLA: {disc.urla_value || "N/A"}</span> • <span>Credit: {disc.credit_value || "N/A"}</span>
                                  </div>
                                  {disc.impact && <p className="text-xs text-amber-700 mt-0.5">Impact: {disc.impact}</p>}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* URLA vs Documents */}
                  {report1_Financials.crossVerification.urla_vs_documents && (
                    <div className="mb-6">
                      <h3 className="text-base font-semibold text-slate-800 mb-3 flex items-center gap-2">
                        <FileSearch className="w-4 h-4 text-sky-600" /> URLA vs Documents
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Income Verification */}
                        {report1_Financials.crossVerification.urla_vs_documents.income_verification && (
                          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                            <h4 className="text-sm font-semibold text-slate-800 mb-2">Income Verification</h4>
                            <div className="text-sm text-slate-700 space-y-1">
                              <div className="flex justify-between">
                                <span>URLA Stated:</span>
                                <span className="font-medium">${formatNumber(report1_Financials.crossVerification.urla_vs_documents.income_verification.urla_stated || 0)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Document Verified:</span>
                                <span className="font-medium">${formatNumber(report1_Financials.crossVerification.urla_vs_documents.income_verification.document_verified || 0)}</span>
                              </div>
                              <div className="flex justify-between pt-1 border-t border-slate-200">
                                <span>Variance:</span>
                                <span className={`font-semibold ${report1_Financials.crossVerification.urla_vs_documents.income_verification.variance_percent > 5 ? 'text-red-600' : 'text-green-600'}`}>
                                  {report1_Financials.crossVerification.urla_vs_documents.income_verification.variance_percent}%
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                        {/* Asset Verification */}
                        {report1_Financials.crossVerification.urla_vs_documents.asset_verification && (
                          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                            <h4 className="text-sm font-semibold text-slate-800 mb-2">Asset Verification</h4>
                            <div className="text-sm text-slate-700 space-y-1">
                              <div className="flex justify-between">
                                <span>URLA Stated:</span>
                                <span className="font-medium">${formatNumber(report1_Financials.crossVerification.urla_vs_documents.asset_verification.urla_stated || 0)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Document Verified:</span>
                                <span className="font-medium">${formatNumber(report1_Financials.crossVerification.urla_vs_documents.asset_verification.document_verified || 0)}</span>
                              </div>
                              <div className="flex justify-between pt-1 border-t border-slate-200">
                                <span>Status:</span>
                                <span className={`font-semibold ${report1_Financials.crossVerification.urla_vs_documents.asset_verification.status === 'Verified' ? 'text-green-600' : 'text-amber-600'}`}>
                                  {report1_Financials.crossVerification.urla_vs_documents.asset_verification.status || 'Pending'}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Credit vs Documents */}
                  {report1_Financials.crossVerification.credit_vs_documents && (
                    <div className="mb-6">
                      <h3 className="text-base font-semibold text-slate-800 mb-3 flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-sky-600" /> Credit vs Documents
                      </h3>
                      <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                        {report1_Financials.crossVerification.credit_vs_documents.liability_matches?.length > 0 && (
                          <div className="mb-3">
                            <h4 className="text-sm font-semibold text-green-700 mb-2">Liability Matches</h4>
                            <ul className="space-y-1">
                              {report1_Financials.crossVerification.credit_vs_documents.liability_matches.map((match, idx) => (
                                <li key={idx} className="text-sm text-slate-700 flex justify-between">
                                  <span>{match.creditor}</span>
                                  <span className="font-medium">${formatNumber(match.balance || 0)}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {report1_Financials.crossVerification.credit_vs_documents.undisclosed_debts?.length > 0 && (
                          <div className="pt-3 border-t border-slate-200">
                            <h4 className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" /> Undisclosed Debts
                            </h4>
                            <ul className="space-y-1">
                              {report1_Financials.crossVerification.credit_vs_documents.undisclosed_debts.map((debt, idx) => (
                                <li key={idx} className="text-sm text-red-700 flex justify-between">
                                  <span>{debt.creditor}</span>
                                  <span className="font-medium">${formatNumber(debt.balance || 0)} @ ${formatNumber(debt.payment || 0)}/mo</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Key Findings Summary */}
                  {report1_Financials.crossVerification.key_findings?.length > 0 && (
                    <div className="bg-sky-50 p-4 rounded-lg border border-sky-200">
                      <h4 className="text-sm font-semibold text-sky-800 mb-2">Key Findings</h4>
                      <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                        {report1_Financials.crossVerification.key_findings.map((finding, idx) => (
                          <li key={idx}>{finding}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* 11. Credit Report Deep Dive */}
              {report1_Financials.creditDeepDive && Object.keys(report1_Financials.creditDeepDive).length > 0 && (
                <div className="mt-10 break-before-page">
                  <h2 className="text-lg font-bold text-sky-800 uppercase border-b-2 border-sky-200 pb-2 mb-4 flex items-center gap-2">
                    <CreditCard className="w-5 h-5" /> 11. Credit Report Deep Dive
                  </h2>

                  {/* Credit Scores */}
                  {report1_Financials.creditDeepDive.credit_scores && (
                    <div className="mb-6">
                      <h3 className="text-base font-semibold text-slate-800 mb-3">Credit Scores</h3>
                      <div className="grid grid-cols-3 gap-4">
                        {['equifax', 'experian', 'transunion'].map((bureau) => {
                          const score = report1_Financials.creditDeepDive.credit_scores[bureau];
                          if (!score) return null;
                          const scoreValue = typeof score === 'object' ? score.score : score;
                          const scoreColor = scoreValue >= 740 ? 'text-green-600' : scoreValue >= 670 ? 'text-amber-600' : 'text-red-600';
                          const bgColor = scoreValue >= 740 ? 'bg-green-50 border-green-200' : scoreValue >= 670 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200';
                          return (
                            <div key={bureau} className={`p-4 rounded-lg border ${bgColor} text-center`}>
                              <div className="text-xs uppercase text-slate-500 mb-1">{bureau}</div>
                              <div className={`text-3xl font-extrabold ${scoreColor}`}>{scoreValue || 'N/A'}</div>
                              {typeof score === 'object' && score.date && (
                                <div className="text-xs text-slate-500 mt-1">{score.date}</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {report1_Financials.creditDeepDive.credit_scores.middle_score && (
                        <div className="mt-3 text-center">
                          <span className="text-sm text-slate-600">Middle Score: </span>
                          <span className="text-lg font-bold text-slate-800">{report1_Financials.creditDeepDive.credit_scores.middle_score}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Trade Accounts Summary */}
                  {report1_Financials.creditDeepDive.trade_accounts && (
                    <div className="mb-6">
                      <h3 className="text-base font-semibold text-slate-800 mb-3">Trade Accounts Summary</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 text-center">
                          <div className="text-2xl font-bold text-slate-800">{report1_Financials.creditDeepDive.trade_accounts.total_accounts || 0}</div>
                          <div className="text-xs text-slate-500">Total Accounts</div>
                        </div>
                        <div className="bg-green-50 p-3 rounded-lg border border-green-200 text-center">
                          <div className="text-2xl font-bold text-green-700">{report1_Financials.creditDeepDive.trade_accounts.open_accounts || 0}</div>
                          <div className="text-xs text-green-600">Open</div>
                        </div>
                        <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 text-center">
                          <div className="text-2xl font-bold text-slate-600">{report1_Financials.creditDeepDive.trade_accounts.closed_accounts || 0}</div>
                          <div className="text-xs text-slate-500">Closed</div>
                        </div>
                        <div className="bg-sky-50 p-3 rounded-lg border border-sky-200 text-center">
                          <div className="text-2xl font-bold text-sky-700">{report1_Financials.creditDeepDive.trade_accounts.avg_age_months || 0} mo</div>
                          <div className="text-xs text-sky-600">Avg Age</div>
                        </div>
                      </div>

                      {/* Account Details */}
                      {report1_Financials.creditDeepDive.trade_accounts.accounts?.length > 0 && (
                        <div className="border border-slate-200 rounded-lg overflow-hidden shadow-sm">
                          <table className="w-full text-sm">
                            <thead className="bg-slate-100">
                              <tr>
                                <th className="p-3 text-left text-sm font-semibold text-slate-700">Creditor</th>
                                <th className="p-3 text-left text-sm font-semibold text-slate-700">Type</th>
                                <th className="p-3 text-right text-sm font-semibold text-slate-700">Balance</th>
                                <th className="p-3 text-right text-sm font-semibold text-slate-700">Limit</th>
                                <th className="p-3 text-right text-sm font-semibold text-slate-700">Payment</th>
                                <th className="p-3 text-center text-sm font-semibold text-slate-700">Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200">
                              {report1_Financials.creditDeepDive.trade_accounts.accounts.map((acct, idx) => (
                                <tr key={idx} className="hover:bg-slate-50">
                                  <td className="p-3 text-slate-700 font-medium">{acct.creditor || acct.name}</td>
                                  <td className="p-3 text-slate-600">{acct.type || acct.account_type}</td>
                                  <td className="p-3 text-right text-slate-700">${formatNumber(acct.balance || 0)}</td>
                                  <td className="p-3 text-right text-slate-600">${formatNumber(acct.limit || acct.credit_limit || 0)}</td>
                                  <td className="p-3 text-right text-slate-700">${formatNumber(acct.payment || acct.monthly_payment || 0)}</td>
                                  <td className="p-3 text-center">
                                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${acct.status === 'Current' || acct.status === 'Open' ? 'bg-green-100 text-green-800' : acct.status === 'Closed' ? 'bg-slate-100 text-slate-600' : 'bg-red-100 text-red-800'}`}>
                                      {acct.status}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Payment History */}
                  {report1_Financials.creditDeepDive.payment_history && (
                    <div className="mb-6">
                      <h3 className="text-base font-semibold text-slate-800 mb-3">Payment History</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-green-50 p-4 rounded-lg border border-green-200 text-center">
                          <div className="text-3xl font-bold text-green-700">{report1_Financials.creditDeepDive.payment_history.on_time_percent || 0}%</div>
                          <div className="text-xs text-green-600">On-Time</div>
                        </div>
                        <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 text-center">
                          <div className="text-3xl font-bold text-amber-700">{report1_Financials.creditDeepDive.payment_history.late_30_count || 0}</div>
                          <div className="text-xs text-amber-600">30 Days Late</div>
                        </div>
                        <div className="bg-orange-50 p-4 rounded-lg border border-orange-200 text-center">
                          <div className="text-3xl font-bold text-orange-700">{report1_Financials.creditDeepDive.payment_history.late_60_count || 0}</div>
                          <div className="text-xs text-orange-600">60 Days Late</div>
                        </div>
                        <div className="bg-red-50 p-4 rounded-lg border border-red-200 text-center">
                          <div className="text-3xl font-bold text-red-700">{report1_Financials.creditDeepDive.payment_history.late_90_plus_count || 0}</div>
                          <div className="text-xs text-red-600">90+ Days Late</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Derogatory Marks */}
                  {report1_Financials.creditDeepDive.derogatory_marks && (
                    <div className="mb-6">
                      <h3 className="text-base font-semibold text-slate-800 mb-3 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-600" /> Derogatory Marks
                      </h3>
                      {(report1_Financials.creditDeepDive.derogatory_marks.items?.length > 0 ||
                        report1_Financials.creditDeepDive.derogatory_marks.collections?.length > 0 ||
                        report1_Financials.creditDeepDive.derogatory_marks.public_records?.length > 0) ? (
                        <div className="space-y-4">
                          {/* Collections */}
                          {report1_Financials.creditDeepDive.derogatory_marks.collections?.length > 0 && (
                            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                              <h4 className="text-sm font-semibold text-red-800 mb-2">Collections ({report1_Financials.creditDeepDive.derogatory_marks.collections.length})</h4>
                              <ul className="space-y-2">
                                {report1_Financials.creditDeepDive.derogatory_marks.collections.map((item, idx) => (
                                  <li key={idx} className="text-sm text-slate-700 flex justify-between">
                                    <span>{item.creditor || item.agency}</span>
                                    <span className="font-medium text-red-700">${formatNumber(item.balance || item.amount || 0)}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {/* Public Records */}
                          {report1_Financials.creditDeepDive.derogatory_marks.public_records?.length > 0 && (
                            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                              <h4 className="text-sm font-semibold text-red-800 mb-2">Public Records</h4>
                              <ul className="space-y-2">
                                {report1_Financials.creditDeepDive.derogatory_marks.public_records.map((item, idx) => (
                                  <li key={idx} className="text-sm text-slate-700">
                                    <span className="font-medium">{item.type}</span>
                                    {item.date && <span className="text-slate-500"> - {item.date}</span>}
                                    {item.amount && <span className="text-red-700 ml-2">${formatNumber(item.amount)}</span>}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {/* Generic Items */}
                          {report1_Financials.creditDeepDive.derogatory_marks.items?.length > 0 && (
                            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                              <ul className="space-y-2">
                                {report1_Financials.creditDeepDive.derogatory_marks.items.map((item, idx) => (
                                  <li key={idx} className="text-sm text-slate-700">
                                    <span className="font-medium">{item.type || item.description}</span>
                                    {item.creditor && <span className="text-slate-500"> - {item.creditor}</span>}
                                    {item.amount && <span className="text-red-700 ml-2">${formatNumber(item.amount)}</span>}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="bg-green-50 p-4 rounded-lg border border-green-200 text-center">
                          <Check className="w-6 h-6 text-green-600 mx-auto mb-2" />
                          <p className="text-sm text-green-700 font-medium">No derogatory marks found</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Inquiries */}
                  {report1_Financials.creditDeepDive.inquiries && (
                    <div className="mb-6">
                      <h3 className="text-base font-semibold text-slate-800 mb-3">Credit Inquiries</h3>
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 text-center">
                          <div className="text-2xl font-bold text-slate-800">{report1_Financials.creditDeepDive.inquiries.total || 0}</div>
                          <div className="text-xs text-slate-500">Total Inquiries (24 mo)</div>
                        </div>
                        <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 text-center">
                          <div className="text-2xl font-bold text-slate-800">{report1_Financials.creditDeepDive.inquiries.last_6_months || 0}</div>
                          <div className="text-xs text-slate-500">Last 6 Months</div>
                        </div>
                      </div>
                      {report1_Financials.creditDeepDive.inquiries.recent?.length > 0 && (
                        <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                          <h4 className="text-sm font-semibold text-slate-700 mb-2">Recent Inquiries</h4>
                          <ul className="space-y-1">
                            {report1_Financials.creditDeepDive.inquiries.recent.map((inq, idx) => (
                              <li key={idx} className="text-sm text-slate-600 flex justify-between">
                                <span>{inq.creditor || inq.company}</span>
                                <span className="text-slate-500">{inq.date}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Credit Utilization */}
                  {report1_Financials.creditDeepDive.credit_utilization && (
                    <div className="mb-6">
                      <h3 className="text-base font-semibold text-slate-800 mb-3">Credit Utilization</h3>
                      <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-sm text-slate-600">Overall Utilization</span>
                          <span className={`text-2xl font-bold ${
                            (report1_Financials.creditDeepDive.credit_utilization.overall_percent || 0) <= 30
                              ? 'text-green-600'
                              : (report1_Financials.creditDeepDive.credit_utilization.overall_percent || 0) <= 50
                                ? 'text-amber-600'
                                : 'text-red-600'
                          }`}>
                            {report1_Financials.creditDeepDive.credit_utilization.overall_percent || 0}%
                          </span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-3 mb-3">
                          <div
                            className={`h-3 rounded-full ${
                              (report1_Financials.creditDeepDive.credit_utilization.overall_percent || 0) <= 30
                                ? 'bg-green-500'
                                : (report1_Financials.creditDeepDive.credit_utilization.overall_percent || 0) <= 50
                                  ? 'bg-amber-500'
                                  : 'bg-red-500'
                            }`}
                            style={{ width: `${Math.min(report1_Financials.creditDeepDive.credit_utilization.overall_percent || 0, 100)}%` }}
                          ></div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-600">Total Balance:</span>
                            <span className="font-medium text-slate-800">${formatNumber(report1_Financials.creditDeepDive.credit_utilization.total_balance || 0)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-600">Total Limit:</span>
                            <span className="font-medium text-slate-800">${formatNumber(report1_Financials.creditDeepDive.credit_utilization.total_limit || 0)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Analysis Notes */}
                  {report1_Financials.creditDeepDive.analysis_notes && (
                    <div className="bg-sky-50 p-4 rounded-lg border border-sky-200">
                      <h4 className="text-sm font-semibold text-sky-800 mb-2">Analysis Notes</h4>
                      {Array.isArray(report1_Financials.creditDeepDive.analysis_notes) ? (
                        <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                          {report1_Financials.creditDeepDive.analysis_notes.map((note, idx) => (
                            <li key={idx}>{note}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-slate-700">{report1_Financials.creditDeepDive.analysis_notes}</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ==================== REPORT 2: AUDIT REVIEW ==================== */}
            <div className="p-8 break-before-page border-t-8 border-slate-100">
              {/* Audit Header */}
              <div className="bg-white text-slate-900 pb-6 mb-8 border-b-4 border-slate-900">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-4">
                    <Siren className="w-12 h-12 text-red-600" />
                    <div>
                      <h1 className="text-3xl font-extrabold text-slate-900">Underwriting Audit</h1>
                      <p className="text-sm text-slate-500">Internal Forensic Analysis • {report2_Audit.metadata.institution}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-4xl font-bold text-red-600">{report2_Audit.metadata.risk}</div>
                    <div className="text-xs uppercase tracking-wide text-slate-500">Risk Rating</div>
                  </div>
                </div>
              </div>

              <div className="space-y-10">

                {/* Executive Summary */}
                <div>
                  <h3 className="text-lg font-bold text-slate-800 uppercase mb-4 flex items-center gap-3 border-b-2 border-slate-200 pb-3">
                    <ClipboardList className="w-5 h-5 text-slate-500" /> Executive Summary
                  </h3>
                  <div className="bg-slate-50 p-6 rounded-lg border-l-4 border-red-600 shadow-sm">
                    <p className="text-base text-slate-700 italic mb-4">"{report2_Audit.executive_summary.assessment}"</p>
                    <div className="inline-block bg-red-600 text-white text-xs font-semibold px-3 py-1.5 rounded-md">
                      Recommendation: {report2_Audit.metadata.status}
                    </div>
                  </div>
                </div>

                {/* Critical Findings */}
                <div>
                  <h3 className="text-lg font-bold text-slate-800 uppercase mb-4 flex items-center gap-3 border-b-2 border-slate-200 pb-3">
                    <AlertTriangle className="w-5 h-5 text-red-600" /> Critical Findings
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                    {report2_Audit.critical_findings.map((f, i) => (
                      <div key={i} className="border border-slate-200 rounded-lg p-4 shadow-sm flex gap-4 items-start">
                        <div className="shrink-0 pt-1">
                          <span className={`text-xs font-semibold px-2 py-1 rounded-full uppercase ${f.severity === 'CRITICAL' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>
                            {f.severity}
                          </span>
                        </div>
                        <div className="space-y-1">
                          <h4 className="text-base font-semibold text-slate-800 break-words">{f.id}: {f.title}</h4>
                          <p className="text-sm text-slate-700">{f.desc}</p>
                          <p className="text-xs text-slate-500 italic">Impact: {f.impact}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Data Quality & Compliance */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-lg font-bold text-slate-800 uppercase mb-4 flex items-center gap-3 border-b-2 border-slate-200 pb-3">
                      <FileSearch className="w-5 h-5 text-slate-500" /> Data Quality Gaps
                    </h3>
                    <ul className="space-y-3">
                      {report2_Audit.data_quality.map((d, idx) => (
                        <li key={idx} className="text-sm text-slate-700">
                          <span className="font-semibold text-slate-800 block">{d.id}: {d.title}</span>
                          {d.details}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-800 uppercase mb-4 flex items-center gap-3 border-b-2 border-slate-200 pb-3">
                      <Scale className="w-5 h-5 text-slate-500" /> Compliance Issues
                    </h3>
                    <ul className="space-y-3">
                      {report2_Audit.compliance_issues.map((c, idx) => (
                        <li key={idx} className="text-sm text-slate-700">
                          <span className="font-semibold text-slate-800 block">{c.id}: {c.title}</span>
                          {c.desc}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* DTI Stress Test */}
                <div>
                  <h3 className="text-lg font-bold text-slate-800 uppercase mb-4 flex items-center gap-3 border-b-2 border-slate-200 pb-3">
                    <BarChart className="w-5 h-5 text-slate-500" /> DTI Stress Test
                  </h3>
                  <div className="grid grid-cols-2 gap-6 text-center">
                    <div className="bg-white border-2 border-amber-300 rounded-lg p-6 shadow-md">
                      <div className="text-xs font-semibold text-slate-500 uppercase mb-2">{report2_Audit.dti_analysis.optimistic.name}</div>
                      <div className="text-4xl font-extrabold text-amber-500">{report2_Audit.dti_analysis.optimistic.dti}%</div>
                      <div className="text-xs text-slate-600 mt-2">Income: ${formatNumber(report2_Audit.dti_analysis.optimistic.income)}</div>
                    </div>
                    <div className="bg-white border-2 border-red-300 rounded-lg p-6 shadow-md">
                      <div className="text-xs font-semibold text-red-500 uppercase mb-2">{report2_Audit.dti_analysis.conservative.name}</div>
                      <div className="text-4xl font-extrabold text-red-700">{report2_Audit.dti_analysis.conservative.dti}%</div>
                      <div className="text-xs text-red-600 mt-2">Income: ${formatNumber(report2_Audit.dti_analysis.conservative.income)}</div>
                    </div>
                  </div>
                </div>

                {/* Conditions Precedent */}
                <div>
                  <h3 className="text-lg font-bold text-slate-800 uppercase mb-4 flex items-center gap-3 border-b-2 border-slate-200 pb-3">
                    <Check className="w-5 h-5 text-slate-500" /> Conditions Precedent
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-red-50 border-l-4 border-red-500 p-5 rounded-r-lg">
                      <h4 className="text-sm font-semibold text-red-800 uppercase mb-3">Tier 1: Critical (Before Approval)</h4>
                      <ul className="list-decimal list-inside space-y-2 text-sm text-slate-700">
                        {report2_Audit.conditions_precedent.tier1.map((c, idx) => (
                          <li key={idx} className="text-sm text-slate-700">{c}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-amber-50 border-l-4 border-amber-500 p-5 rounded-r-lg">
                      <h4 className="text-sm font-semibold text-amber-800 uppercase mb-3">Tier 2: Required for Closing</h4>
                      <ul className="list-decimal list-inside space-y-2 text-sm text-slate-700">
                        {report2_Audit.conditions_precedent.tier2.map((c, idx) => (
                          <li key={idx} className="text-sm text-slate-700">{c}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Final Rec & Cert */}
                <div className="bg-slate-800 text-white p-6 rounded-lg mt-10">
                  <div className="font-semibold text-base uppercase mb-3 text-sky-300">Final Recommendation: {report2_Audit.final_recommendation.decision}</div>
                  <p className="mb-3 text-sm text-slate-300">{report2_Audit.final_recommendation.rationale}</p>
                  <div className="border-t border-slate-600 pt-3 mt-4 text-xs text-slate-500 italic">                    Certification: {report2_Audit.certification}
                  </div>
                </div>

              </div>
            </div>

            {/* ==================== REPORT 3: FORENSIC ANALYSIS ==================== */}
            <div className="p-8 break-before-page border-t-8 border-slate-100">
              <div className="bg-white text-slate-900 pb-6 mb-8 border-b-4 border-slate-900">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-4">
                    <FileSearch className="w-12 h-12 text-sky-600" />
                    <div>
                      <h1 className="text-3xl font-extrabold text-slate-900">Forensic Financial Report</h1>
                      <p className="text-sm text-slate-500">
                        Borrower: {report3_Forensic.applicants?.[0]?.name || "Borrower"} • Source: Gemini Forensic Extract
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-slate-600">Risk Flags</div>
                    <div className="text-xs text-slate-500">Liquidity • Commingling • Trading Volatility</div>
                  </div>
                </div>
              </div>

              <div className="space-y-10">
                {/* Applicants & Business */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="border border-slate-200 rounded-xl p-4 shadow-sm">
                    <h3 className="text-lg font-bold text-slate-800 uppercase mb-3 flex items-center gap-2">
                      <ClipboardList className="w-5 h-5 text-sky-600" /> Applicants
                    </h3>
                    <div className="space-y-3 text-sm text-slate-700">
                      {report3_Forensic.applicants.map((a, idx) => (
                        <div key={idx} className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                          <p className="font-semibold text-slate-900">{a.name}</p>
                          <p className="text-slate-600">{a.role}</p>
                          <p className="text-slate-500 text-xs">Address: {a.current_address}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="border border-slate-200 rounded-xl p-4 shadow-sm">
                    <h3 className="text-lg font-bold text-slate-800 uppercase mb-3 flex items-center gap-2">
                      <BarChart className="w-5 h-5 text-sky-600" /> Business Overview
                    </h3>
                    {report3_Forensic.businesses.map((biz, idx) => (
                      <div key={idx} className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-700 space-y-2">
                        <div className="flex justify-between">
                          <span className="font-semibold text-slate-800">{biz.name}</span>
                          <span className="text-xs bg-sky-100 text-sky-700 px-2 py-0.5 rounded-full">EIN: {biz.ein}</span>
                        </div>
                        <p className="text-slate-600">{biz.type}</p>
                        <div className="text-xs text-slate-500 space-y-1">
                          <p>State: {biz.formation?.state || "N/A"}</p>
                          <p>File #: {biz.formation?.file_number || "N/A"}</p>
                          <p>Formed: {biz.formation?.formation_date || "N/A"}</p>
                        </div>
                        <div className="text-xs text-slate-600">
                          Ownership: {biz.formation?.ownership?.map((o) => `${o.partner} (${o.percentage}%)`).join(", ")}
                        </div>
                        <div className="text-xs text-slate-600">
                          2024 Net (1065): {formatNumber(biz.tax_return_summaries?.[0]?.summary?.net_income_loss_total ?? biz.tax_return_2024?.form_1065_summary?.ordinary_business_income_loss ?? 0)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Banking */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="border border-slate-200 rounded-xl p-4 shadow-sm">
                    <h3 className="text-lg font-bold text-slate-800 uppercase mb-3 flex items-center gap-2">
                      <FileSearch className="w-5 h-5 text-sky-600" /> Business Banking
                    </h3>
                    {report3_Forensic.bank.business_accounts?.map((acct, idx) => (
                      <div key={idx} className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-700 space-y-2">
                        <div className="flex justify-between">
                          <span className="font-semibold text-slate-800">{acct.institution}</span>
                          <span className="text-xs text-slate-500">{acct.account_type} • •{acct.account_last4 || "----"}</span>
                        </div>
                        {acct.annual_summaries?.[0] && (
                          <div className="text-xs text-slate-600 space-y-1">
                            <p>Total Deposits: ${formatNumber(acct.annual_summaries[0].total_deposits)}</p>
                            <p>Total Withdrawals: ${formatNumber(acct.annual_summaries[0].total_withdrawals)}</p>
                            <p>Net Cash Flow: ${formatNumber(acct.annual_summaries[0].net_cash_flow)}</p>
                            <p>Ending Balance: ${acct.annual_summaries[0].ending_balance?.toLocaleString?.() || "-"}</p>
                          </div>
                        )}
                        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">{acct.notes}</p>
                      </div>
                    ))}
                  </div>

                  <div className="border border-slate-200 rounded-xl p-4 shadow-sm">
                    <h3 className="text-lg font-bold text-slate-800 uppercase mb-3 flex items-center gap-2">
                      <Cloud className="w-5 h-5 text-sky-600" /> Personal Banking
                    </h3>
                    <div className="space-y-3">
                      {report3_Forensic.bank.personal_accounts?.map((acct, idx) => (
                        <div key={idx} className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-700 space-y-2">
                          <div className="flex justify-between">
                            <span className="font-semibold text-slate-800">{acct.institution}</span>
                            <span className="text-xs text-slate-500">{acct.account_type} • •{acct.account_last4 || "----"}</span>
                          </div>
                          {acct.monthly_snapshots?.map((snap, snapIdx) => (
                            <div key={snapIdx} className="text-xs text-slate-600 flex justify-between">
                              <span>{snap.period}</span>
                              <span className="text-right">
                                +${formatNumber(snap.deposits)} / -${formatNumber(snap.withdrawals)} (End: ${formatNumber(snap.ending_balance)})
                              </span>
                            </div>
                          ))}
                          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">{acct.notes}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Investments & Income */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="border border-slate-200 rounded-xl p-4 shadow-sm">
                    <h3 className="text-lg font-bold text-slate-800 uppercase mb-3 flex items-center gap-2">
                      <BarChart className="w-5 h-5 text-sky-600" /> Investment Accounts
                    </h3>
                    <div className="space-y-3">
                      {report3_Forensic.investments.map((inv, idx) => (
                        <div key={idx} className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-700 space-y-2">
                          <div className="flex justify-between">
                            <span className="font-semibold text-slate-800">{inv.account}</span>
                            <span className="text-xs text-slate-500">{inv.owner}</span>
                          </div>
                          <div className="text-xs text-slate-600 space-y-1">
                            {inv.value_history?.map((vh, vidx) => (
                              <div key={vidx} className="flex justify-between">
                                <span>{vh.as_of}</span>
                                <span>${formatNumber(vh.net_value)}</span>
                              </div>
                            ))}
                          </div>
                          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">{inv.notes}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="border border-slate-200 rounded-xl p-4 shadow-sm">
                    <h3 className="text-lg font-bold text-slate-800 uppercase mb-3 flex items-center gap-2">
                      <Scale className="w-5 h-5 text-sky-600" /> Income & Liabilities
                    </h3>
                    <div className="space-y-3 text-sm text-slate-700">
                      <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-1">
                        <p className="font-semibold text-slate-800">Qualifying Income</p>
                        <p className="text-xs text-slate-600">
                          W-2 Gross Monthly: $
                          {(() => {
                            const monthlyFromQual =
                              report3_Forensic.income?.qualifying_income_summary?.base_employment_income_gross_monthly;
                            const annualW2 = report3_Forensic.income?.personal_tax_return_summaries?.[0]?.summary?.w2_wages;
                            const monthlyFromW2 = annualW2 ? annualW2 / 12 : null;
                            const val = monthlyFromQual || monthlyFromW2 || 0;
                            return Number(val).toLocaleString();
                          })()}
                        </p>
                        <p className="text-xs text-slate-500">
                          {report3_Forensic.income?.qualifying_income_summary?.notes ||
                            "Qualifying income sourced from W-2; rental cash flow reflected in liabilities."}
                        </p>
                      </div>
                      <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-1">
                        <p className="font-semibold text-slate-800">Tax Snapshot (1040)</p>
                        <p className="text-xs text-slate-600">
                          AGI: $
                          {Number(
                            report3_Forensic.income?.personal_tax_return_summaries?.[0]?.summary?.agi || 0
                          ).toLocaleString()}
                        </p>
                        <p className="text-xs text-slate-600">
                          Capital Gains (Net): $
                          {Number(
                            report3_Forensic.income?.personal_tax_return_summaries?.[0]?.summary?.capital_gains_net || 0
                          ).toLocaleString()}
                        </p>
                        <p className="text-xs text-slate-600">
                          Net Investment Income Tax: $
                          {Number(
                            report3_Forensic.income?.personal_tax_return_summaries?.[0]?.summary?.other_items
                              ?.net_investment_income_tax || 0
                          ).toLocaleString()}
                        </p>
                        <p className="text-xs text-slate-600">
                          Additional Medicare Tax: $
                          {Number(
                            report3_Forensic.income?.personal_tax_return_summaries?.[0]?.summary?.other_items
                              ?.additional_medicare_tax || 0
                          ).toLocaleString()}
                        </p>
                        <p className="text-xs text-slate-600">
                          HSA Excess Contribution Penalty:
                          {" "}
                          {report3_Forensic.income?.personal_tax_return_summaries?.[0]?.summary?.other_items
                            ?.hsa_excess_contrib_penalty_flag
                            ? "Yes"
                            : "No"}
                        </p>
                      </div>
                      <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-1">
                        <p className="font-semibold text-slate-800">Liabilities</p>
                        <p className="text-xs text-slate-600">
                          Total Estimated Monthly Mortgage Debt: $
                          {Number(
                            report3_Forensic.liabilities?.total_estimated_monthly_mortgage_debt || 0
                          ).toLocaleString()}
                        </p>
                        {report3_Forensic.liabilities?.regions_bank_estimated_payment !== undefined && (
                          <p className="text-xs text-slate-600">
                            Regions Bank Estimated: $
                            {Number(report3_Forensic.liabilities?.regions_bank_estimated_payment || 0).toLocaleString()}
                          </p>
                        )}
                        <p className="text-xs text-slate-600">
                          {report3_Forensic.liabilities?.other_debts ||
                            report3_Forensic.liabilities?.mortgage_obligations ||
                            report3_Forensic.liabilities?.notes ||
                            "Liability details pending credit report."}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Risks & Follow-ups */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="border border-slate-200 rounded-xl p-4 shadow-sm">
                    <h3 className="text-lg font-bold text-slate-800 uppercase mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-600" /> Forensic Risks
                    </h3>
                    <ul className="space-y-3 text-sm text-slate-700">
                      {report3_Forensic.risks.map((risk, idx) => (
                        <li key={idx} className="p-3 rounded-lg bg-red-50 border border-red-100">
                          <div className="flex justify-between items-center">
                            <span className="font-semibold text-slate-800">
                              {risk.risk_id} — {risk.category}
                            </span>
                            <span className="text-xs font-bold text-red-600 uppercase">{risk.severity}</span>
                          </div>
                          <p className="text-xs text-slate-700 mt-1">{risk.description}</p>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="border border-slate-200 rounded-xl p-4 shadow-sm">
                    <h3 className="text-lg font-bold text-slate-800 uppercase mb-3 flex items-center gap-2">
                      <ClipboardList className="w-5 h-5 text-sky-600" /> Required Follow-ups
                    </h3>
                    <ul className="space-y-2 text-sm text-slate-700 list-disc list-inside">
                      {report3_Forensic.followUps.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Documents Referenced */}
                {report3_Forensic.documents?.length ? (
                  <div className="border border-slate-200 rounded-xl p-4 shadow-sm">
                    <h3 className="text-lg font-bold text-slate-800 uppercase mb-3 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-sky-600" /> Documents Referenced
                    </h3>
                    <p className="text-sm text-slate-700">{report3_Forensic.documents.join(", ")}</p>
                  </div>
                ) : null}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

SampleSummaryModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  autoDownload: PropTypes.bool,
};

export default SampleSummaryModal;
